// Third Party
import { clone } from "lodash-es";

// Components
import FinanceDataTab from "@DataPages/components/OEM/OEMTabs/FinancialDataTab";
import OEMDocumentsTab from "@DataPages/components/OEM/OEMTabs/OEMDocumentsTab";
import EmptyBody from "@Layouts/components/Basics/EmptyBody";

// Helpers
import { getSanitatedLocationOEMs } from "@Common/helpers/OEMs";

// Images
import empty from "@/assets/images/commons/empty.png";

// Types
import { ILocation } from "@Common/interfaces/location";
import { ICommissionCycle } from "@Common/api/types/CommissionCycle";

type OEMTabBodyProps = {
	location: ILocation;
	tab: string;
	selected: ICommissionCycle;
};

type Cell = {
	_id: string;
	columnId: string;
	value: string | number;
	oldValue?: string | number;
};

type Row = {
	_id: string;
	sectionType: "ROW";
	lineNumber: string;
	cells: Cell[];
	isCurrentLocation?: boolean;
};

export type RowArray = Row[];

export type FilterRankingDataPageProps = {
	content: RowArray;
	tableInformation: {
		rankingsMapping: {
			ranking: string;
			region: string;
			rankingYTD?: string;
		};
	};
	applyFilter?: boolean;
	dealerCode?: string;
	franchise?: string;
};

const NoData = () => {
	return (
		<EmptyBody
			image={empty}
			title={"No OEMs found"}
			imageStyles={{ width: "330px", marginTop: "65px" }}
			bodyHeight={"320px"}
			sx={{ height: "500px !important", boxShadow: "none !important" }}
		/>
	);
};

/********************************************** HELPERS **********************************************/
const getRegionValue = (data: Row[], regionColumnId: string) => {
	for (const row of data) {
		if (row?.isCurrentLocation && row?.cells) {
			const regionCell = row.cells.find((cell) => cell.columnId === regionColumnId.toString());
			return regionCell ? regionCell.value : null;
		}
	}
	return null;
};

function computeRankings({
	data,
	rankId,
	rankYTDId,
	ranksYTD,
	useOldValues = false,
}: {
	data: RowArray;
	rankId: string;
	rankYTDId?: string;
	ranksYTD?: number[];
	useOldValues?: boolean;
}): RowArray {
	const sortedYTDRanks: typeof ranksYTD = [];
	if (rankYTDId && ranksYTD) {
		ranksYTD.sort((a, b) => a - b);
		sortedYTDRanks.push(...ranksYTD);
	}
	return data.map((entry) => {
		return {
			...entry,
			cells: entry.cells.map((cell) => {
				if (cell.columnId === rankId) {
					cell.value = data.indexOf(entry) + 1;
				}
				if (sortedYTDRanks.length && rankYTDId && cell.columnId === rankYTDId && !cell.oldValue) {
					cell.oldValue = cell.value;
					cell.value = sortedYTDRanks.indexOf(Number(cell.value)) + 1;
				}
				if (useOldValues) {
					cell.value = cell.oldValue ?? cell.value;
					cell.oldValue = undefined; // NOSONAR - this is intentional to effectively clear the attribute
				}
				return cell;
			}),
		};
	});
}

export default function OEMTabBody(props: Readonly<OEMTabBodyProps>) {
	const locationOEMs = getSanitatedLocationOEMs(props.location?.oems);

	const filterRankingDataPage = ({ content, tableInformation, applyFilter = false }: Readonly<FilterRankingDataPageProps>) => {
		const { rankingsMapping } = tableInformation;
		const rankColumnId = rankingsMapping?.ranking;
		const regionColumnId = rankingsMapping?.region;
		const rankYTDColumnId = rankingsMapping?.rankingYTD;
		const data = clone(content);
		const regionValue = getRegionValue(data, regionColumnId);

		const isDataValid = rankingsMapping && regionColumnId && rankColumnId && data?.length;
		if (isDataValid && regionValue && applyFilter) {
			const ytdRanks: number[] = [];
			const result = data.reduce((acc, entry) => {
				if (
					entry.cells.some(
						(cell) => cell.columnId.toString() === regionColumnId.toString() && cell.value.toString() === regionValue.toString()
					)
				) {
					if (rankYTDColumnId) {
						const ytdRank = entry.cells.find((cell) => cell.columnId === rankYTDColumnId)?.value;
						ytdRanks.push(Number(ytdRank));
					}
					return [...acc, entry];
				}
				return acc;
			}, [] as RowArray);

			return computeRankings({ data: result, rankId: rankColumnId, rankYTDId: rankYTDColumnId, ranksYTD: ytdRanks });
		}

		if (!applyFilter && content) {
			return computeRankings({ data: content, rankId: rankColumnId, useOldValues: true });
		}

		return content;
	};

	switch (props.tab) {
		case "Financial Statement":
			return <FinanceDataTab locationOEMs={locationOEMs} locationId={props.location.locationId} selected={props.selected} />;
		case "National Rankings":
			return (
				<OEMDocumentsTab
					locationOEMs={locationOEMs}
					locationId={props.location?.locationId}
					selected={props.selected}
					oemDataTab={"National Rankings"}
					emptyTitle={"Ranking"}
					filterRankingDataPage={filterRankingDataPage}
					tableProps={{
						autoHeight: null,
						maxRows: 25,
						enableCustomScrollbars: true,
						disableHScrollCorrection: false,
						customProps: {
							tableContainerStyle: { height: "auto", width: "100%" },
						},
					}}
				/>
			);
		case "Market Share":
			return (
				<OEMDocumentsTab
					locationOEMs={locationOEMs}
					locationId={props.location.locationId}
					selected={props.selected}
					oemDataTab={"Market Share"}
					tableProps={{
						enableCustomScrollbars: true,
						disableHScrollCorrection: false,
						customProps: {
							tableContainerStyle: { height: "auto", width: "100%" },
						},
					}}
				/>
			);
		case "Training":
			return <NoData />;
		case "SFE":
			return (
				<OEMDocumentsTab
					locationOEMs={locationOEMs}
					locationId={props.location.locationId}
					selected={props.selected}
					oemDataTab={"SFE"}
				/>
			);
		case "CSI":
			return (
				<OEMDocumentsTab
					locationId={props.location.locationId}
					locationOEMs={locationOEMs}
					selected={props.selected}
					oemDataTab={"CSI"}
				/>
			);
		case "EBE":
			return (
				<OEMDocumentsTab
					locationOEMs={locationOEMs}
					selected={props.selected}
					locationId={props.location.locationId}
					oemDataTab={"EBE"}
				/>
			);
		case "Pinnacle":
			return (
				<OEMDocumentsTab
					locationOEMs={locationOEMs}
					locationId={props.location.locationId}
					oemDataTab={"Pinnacle"}
					selected={props.selected}
				/>
			);
		case "PASE":
			return (
				<OEMDocumentsTab
					locationId={props.location.locationId}
					locationOEMs={locationOEMs}
					selected={props.selected}
					oemDataTab={"PASE"}
				/>
			);
		case "SSI":
			return (
				<OEMDocumentsTab
					locationOEMs={locationOEMs}
					selected={props.selected}
					locationId={props.location.locationId}
					oemDataTab={"SSI"}
				/>
			);
		case "Bonus Program":
			return <NoData />;
		default:
			return <></>;
	}
}
